import React, { useEffect, useMemo, useState } from 'react'
import {
  Panel,
  ButtonToolbar,
  Toggle,
  Uploader,
  Col,
  SelectPicker
} from 'rsuite'
import {
  Button,
  FormikErrorMessage,
  Modal,
  Row,
  Section,
  TextInput
} from 'shared'
import { Form, Formik, FormikHelpers, FormikProps } from 'formik'
import { skipToken } from '@reduxjs/toolkit/query'
import { getModalTitleText, notifyError, notifySuccess } from 'utils'
import { baseUrl, getAccessToken } from 'api/axiosInstance'
import { Audience, EstimatedAudience } from '../types'
import {
  AUDIENCE_FORM_FIELDS,
  audienceValidationSchema,
  IAudienceForm,
  mapAudienceToFormValues
} from '../utils'

import {
  useCreateAudienceMutation,
  useGetAudienceCountQuery,
  useGetProvincesQuery,
  useGetCountryQuery,
  useGetDistrictsQuery,
  useGetSubDistrictsQuery,
  useGetCropsQuery
} from '../audienceApiSlice'
import './CreateEditAudience.scss'

interface CreateEditAudienceProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  audienceData: Audience | null;
}

const CreateEditAudiences: React.FC<CreateEditAudienceProps> = ({
  open,
  setOpen,
  isEditMode,
  setIsEditMode,
  audienceData
}) => {
  const {
    NAME,
    DESCRIPTION,
    ACTIVE,
    COUNTRY,
    PROVINCES,
    DISTRICT,
    SUB_DISTRICT,
    CROPS,
    PINCODE
  } = AUDIENCE_FORM_FIELDS

  const [CreateAudience, { isLoading: isSaving }] = useCreateAudienceMutation()
  const [selectedAudienceCount, setselectedAudienceCount] =
    useState<EstimatedAudience>({})
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(
    null
  )
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null)
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(
    null
  )
  const formInitialValues = useMemo(
    () => mapAudienceToFormValues(audienceData),
    [audienceData]
  )

  const { data: CropData, isLoading: isCropDataLoading } = useGetCropsQuery()
  const CropDataOptions = CropData?.map((item: any) => ({
    label: item.name,
    value: item.id
  }))

  const { data: countryData, isLoading: isCountryLoading } =
    useGetCountryQuery()
  const { data: statesData, isLoading: isStatesLoading } = useGetProvincesQuery(
    selectedCountryId ?? skipToken,
    {
      skip: selectedCountryId === null
    }
  )

  const { data: districtsData, isLoading: isDistrictsLoading } =
    useGetDistrictsQuery(selectedStateId ?? skipToken, {
      skip: selectedStateId === null
    })

  const { data: subDistrictsData, isLoading: isSubDistrictsLoading } =
    useGetSubDistrictsQuery(selectedDistrictId ?? skipToken, {
      skip: selectedDistrictId === null
    })

  const generateOptions: any = (data: any | undefined, isLoading: boolean) =>
    isLoading
      ? []
      : data?.map((item: any) => ({
          label: item.name,
          value: item.id
        })) || []

  const countryOptions = generateOptions(countryData, isCountryLoading)
  const stateOptions = generateOptions(statesData, isStatesLoading)
  const districtOptions = generateOptions(districtsData, isDistrictsLoading)
  const subDistrictOptions = generateOptions(
    subDistrictsData,
    isSubDistrictsLoading
  )

  const SyncFormValuesWithParams = ({
    formikProps
  }: {
    formikProps: FormikProps<IAudienceForm>
  }) => {
    useEffect(() => {
      setselectedAudienceCount({
        countryId: formikProps.values.country
          ? String(formikProps.values.country)
          : undefined,
        stateId: formikProps.values.state
          ? String(formikProps.values.state)
          : undefined,
        districtId: formikProps.values.district
          ? String(formikProps.values.district)
          : undefined,
        talukaId: formikProps.values.taluka
          ? String(formikProps.values.taluka)
          : undefined,
        cropId: formikProps.values.crop
          ? String(formikProps.values.crop)
          : undefined
      })
    }, [formikProps.values])

    return null
  }

  const { data: audienceCount } = useGetAudienceCountQuery(
    selectedAudienceCount,
    {
      skip: !selectedAudienceCount?.countryId
    }
  )

  const onSubmit = async (
    formValues: IAudienceForm,
    { setSubmitting }: FormikHelpers<IAudienceForm>
  ) => {
    const payload: Audience = {
      [AUDIENCE_FORM_FIELDS.NAME]: formValues[AUDIENCE_FORM_FIELDS.NAME],
      [AUDIENCE_FORM_FIELDS.DESCRIPTION]:
        formValues[AUDIENCE_FORM_FIELDS.DESCRIPTION],
      [AUDIENCE_FORM_FIELDS.PINCODE]: formValues[AUDIENCE_FORM_FIELDS.PINCODE],
      [AUDIENCE_FORM_FIELDS.ACTIVE]: formValues[AUDIENCE_FORM_FIELDS.ACTIVE],
      country: formValues[AUDIENCE_FORM_FIELDS.COUNTRY]
        ? { id: formValues[AUDIENCE_FORM_FIELDS.COUNTRY] }
        : undefined,
      state: formValues[AUDIENCE_FORM_FIELDS.PROVINCES]
        ? { id: formValues[AUDIENCE_FORM_FIELDS.PROVINCES] }
        : undefined,

      district: formValues[AUDIENCE_FORM_FIELDS.DISTRICT]
        ? { id: formValues[AUDIENCE_FORM_FIELDS.DISTRICT] }
        : undefined,

      taluka: formValues[AUDIENCE_FORM_FIELDS.SUB_DISTRICT]
        ? { id: formValues[AUDIENCE_FORM_FIELDS.SUB_DISTRICT] }
        : undefined,

      crop: formValues[AUDIENCE_FORM_FIELDS.CROPS]
        ? { id: formValues[AUDIENCE_FORM_FIELDS.CROPS] }
        : undefined
    }

    try {
      await CreateAudience(payload).unwrap()
      notifySuccess(
        audienceData
          ? 'Audience updated successfully!'
          : 'Audience created successfully!'
      )
      setOpen(false)
    } catch (error: any) {
      const errorMessage =
        (error as { data?: { message?: string } })?.data?.message ||
        'Failed to save Audience'
      notifyError(errorMessage, { containerId: 'modalToast' })
    } finally {
      setSubmitting(false)
    }
  }

  const uploadApiUrl = `${baseUrl}/batch/importJob`

  const renderFormButtons = (formikProps: FormikProps<IAudienceForm>) => (
    <ButtonToolbar>
      {isEditMode ? (
        <>
          <Button
            className="formButton"
            id="reset"
            appearance="default"
            data-testid="resetButton"
            onClick={() => {
              formikProps?.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="formButton"
            type="submit"
            data-testid="submitButton"
            appearance="primary"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save
          </Button>
        </>
      ) : (
        <>
          <Button
            className="formButton"
            id="reset"
            onClick={() => {
              formikProps.resetForm()
            }}
            disabled
          >
            Reset to default
          </Button>
          <Button
            className="formButton"
            type="submit"
            appearance="primary"
            disabled
          >
            Save changes
          </Button>
        </>
      )}
    </ButtonToolbar>
  )
  return (
    <Modal
      secondary
      open={open}
      onClose={() => setOpen(false)}
      size="calc(70% - 120px)"
      className="audience-modal"
      title={getModalTitleText(
        isSaving,
        !audienceData ? 'Create Audience' : 'Edit Audience'
      )}
      body={
        <div>
          <Formik
            initialValues={formInitialValues}
            validationSchema={audienceValidationSchema}
            enableReinitialize
            onSubmit={onSubmit}
          >
            {(formikProps: FormikProps<IAudienceForm>) => (
              <>
                <SyncFormValuesWithParams formikProps={formikProps} />
                <Form className="create-edit-audience">
                  <Panel>
                    <Section title="Audience Details">
                      <Row>
                        <Col xs={12}>
                          <TextInput
                            formik={formikProps}
                            name={NAME}
                            placeholder="Enter Audience Name"
                            isDisabled={!isEditMode}
                          />
                          <FormikErrorMessage name={NAME} />
                        </Col>
                        <Col xs={12}>
                          <TextInput
                            formik={formikProps}
                            name={DESCRIPTION}
                            placeholder="Enter description here"
                            as="textarea"
                            isDisabled={!isEditMode}
                          />
                          <FormikErrorMessage name={DESCRIPTION} />
                        </Col>
                      </Row>
                    </Section>

                    <Section title="Address Information">
                      <Row>
                        <Col xs={8}>
                          <SelectPicker
                            data={isCountryLoading ? [] : countryOptions}
                            placeholder={
                              isCountryLoading
                                ? 'Loading country...'
                                : 'Select Country'
                            }
                            value={formikProps.values[COUNTRY]}
                            onChange={(value) => {
                              const parsedValue =
                                value !== null ? Number(value) : null
                              formikProps.setFieldValue(COUNTRY, parsedValue)
                              setSelectedCountryId(parsedValue)
                            }}
                            block
                            disabled={!isEditMode}
                          />
                          <FormikErrorMessage name={COUNTRY} />
                        </Col>
                        <Col xs={8}>
                          <SelectPicker
                            data={isStatesLoading ? [] : stateOptions}
                            placeholder={
                              isStatesLoading
                                ? 'Loading states...'
                                : 'Select State'
                            }
                            value={formikProps.values[PROVINCES]}
                            onChange={(value) => {
                              const parsedValue =
                                value !== null ? Number(value) : null
                              formikProps.setFieldValue(PROVINCES, parsedValue)
                              setSelectedStateId(parsedValue)
                            }}
                            block
                            disabled={
                              !formikProps.values[COUNTRY] ||
                              isStatesLoading ||
                              !isEditMode
                            }
                          />
                          <FormikErrorMessage name={PROVINCES} />
                        </Col>
                        <Col xs={8}>
                          <SelectPicker
                            data={isDistrictsLoading ? [] : districtOptions}
                            placeholder={
                              isDistrictsLoading
                                ? 'Loading districts...'
                                : 'Select District'
                            }
                            value={formikProps.values[DISTRICT]}
                            onChange={(value) => {
                              const parsedValue =
                                value !== null ? Number(value) : null
                              formikProps.setFieldValue(DISTRICT, parsedValue)
                              setSelectedDistrictId(parsedValue)
                            }}
                            block
                            disabled={
                              !formikProps.values[PROVINCES] ||
                              isDistrictsLoading ||
                              !isEditMode
                            }
                          />
                          <FormikErrorMessage name={DISTRICT} />
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={8}>
                          <SelectPicker
                            data={
                              isSubDistrictsLoading ? [] : subDistrictOptions
                            }
                            placeholder={
                              isSubDistrictsLoading
                                ? 'Loading Sub Districts...'
                                : 'Select SubDistrict'
                            }
                            value={formikProps.values[SUB_DISTRICT]}
                            onChange={(value) => {
                              const parsedValue =
                                value !== null ? Number(value) : null
                              formikProps.setFieldValue(
                                SUB_DISTRICT,
                                parsedValue
                              )
                            }}
                            block
                            disabled={
                              !formikProps.values[DISTRICT] ||
                              isSubDistrictsLoading ||
                              !isEditMode
                            }
                          />
                          <FormikErrorMessage name={SUB_DISTRICT} />
                        </Col>
                        <Col xs={8}>
                          <TextInput
                            formik={formikProps}
                            name={PINCODE}
                            placeholder="Enter Pincode"
                            isDisabled={!isEditMode}
                          />
                          <FormikErrorMessage name={PINCODE} />
                        </Col>
                        <Col xs={8}>
                          <SelectPicker
                            data={
                              isCropDataLoading ? [] : (CropDataOptions ?? [])
                            }
                            placeholder={
                              isSubDistrictsLoading
                                ? 'Loading Crops...'
                                : 'Select Crops'
                            }
                            value={formikProps.values[CROPS]}
                            onChange={(value) => {
                              const parsedValue =
                                value !== null ? Number(value) : null

                              formikProps.setFieldValue(CROPS, parsedValue)
                            }}
                            block
                            disabled={!isEditMode}
                          />
                          <FormikErrorMessage name={CROPS} />
                        </Col>
                      </Row>
                    </Section>

                    <Section title="Estimated Users">
                      <Row>
                        <Col xs={24} md={12}>
                          <div className="estimated-users-audience">
                            {audienceCount ?? 0}
                          </div>
                        </Col>
                      </Row>
                    </Section>
                    <Section title="Import CSV/Excel File (optional)">
                      <Button
                        type="button"
                        appearance="default"
                        className="download-csv-button"
                        onClick={() => {
                          const link = document.createElement('a')
                          link.href = '/CSV.file.csv'
                          link.download = 'CSV-format.csv'
                          document.body.appendChild(link)
                          link.click()
                          document.body.removeChild(link)
                        }}
                      >
                        Download CSV Format
                      </Button>
                      <Row>
                        <Uploader
                          className="csv-uploader"
                          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                          multiple={false}
                          action={uploadApiUrl}
                          headers={{
                            Authorization: `Bearer ${getAccessToken()}`
                          }}
                          draggable
                          disabled={!isEditMode}
                        >
                          <div
                            style={{
                              height: 200,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <span>
                              Click or Drag files to this area to upload file
                            </span>
                          </div>
                        </Uploader>
                      </Row>
                    </Section>
                    <Section title="Status">
                      <Toggle
                        checked={formikProps.values[ACTIVE]}
                        onChange={() =>
                          formikProps.setFieldValue(
                            ACTIVE,
                            !formikProps.values[ACTIVE]
                          )
                        }
                        disabled={!isEditMode}
                      />
                    </Section>
                  </Panel>
                  <Panel className="panel-buttons">
                    {renderFormButtons(formikProps)}
                  </Panel>
                </Form>
              </>
            )}
          </Formik>
        </div>
      }
    />
  )
}

export default CreateEditAudiences
